import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  istanbulDayString,
  shiftIsoDay,
  t,
  tr,
  type MessageKey,
} from '@aquabox/shared';
import { useMe, useTenantMutations } from '../api/queries';
import {
  saveLucaConnection,
  useLucaConnection,
  useLucaDocuments,
  useLucaMutations,
  useLucaReconciliation,
  type LucaConnection,
  type LucaDocumentKind,
  type LucaShipmentRef,
} from '../api/luca';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Tabs, type TabItem } from '../components/Tabs';
import { useToast } from '../components/Toast';

/**
 * Luca e-Fatura/e-İrsaliye (ADR 2026-10-31) — salt okuma.
 *
 * KİMLİK YALNIZ YAZILIR: TCKN ve parola sunucuya gider, geri gelmez; ekranda maskeli hâli bile
 * gösterilmez (yalnız "bağlı / bağlı değil"). Alanlar react-query'ye girmez (`saveLucaConnection`
 * düz işlev) ve kayıttan sonra temizlenir. Kimlik girişi, etkinleştirme ve kesme yalnız OWNER.
 * Tutarlar sunucudan string gelir; burada toplama/karşılaştırma yapılmaz (kural 4).
 */
export function Luca() {
  const me = useMe();
  const isOwner = me.data?.role === 'OWNER';
  const [tab, setTab] = useState<'documents' | 'reconciliation'>('reconciliation');
  const items: TabItem[] = [
    { value: 'reconciliation', label: t('luca.tab.reconciliation') },
    { value: 'documents', label: t('luca.tab.documents') },
  ];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-secondary)', maxWidth: 720 }}>
        {t('luca.subtitle')}
      </p>
      <ConnectionCard isOwner={isOwner} />
      <Tabs items={items} value={tab} onChange={(v) => setTab(v as typeof tab)} />
      {tab === 'documents' ? <DocumentsPanel /> : <ReconciliationPanel />}
    </div>
  );
}

function errorText(error: unknown): string {
  const message = (error as { message?: string } | null)?.message;
  return message && message.length > 0 ? message : t('error.internal');
}

function lastErrorText(stored: string, detail?: string): string {
  // Kayıt biçimi: KOD | KOD:http:403 | KOD:form | KOD:captcha
  const [code, ...rest] = stored.split(':');
  const extra = detail ?? rest.join(':');
  if (code === 'LUCA_LOGIN_FAILED') {
    if (extra.startsWith('http:')) {
      return t('luca.error.loginHttp', { status: extra.slice(5) });
    }
    if (extra === 'form') return t('luca.error.loginForm');
    if (extra === 'captcha') return t('luca.error.loginCaptcha');
  }
  const key = `luca.error.${code}`;
  return key in tr ? t(key as MessageKey) : t('luca.error.generic');
}

function ConnectionCard({ isOwner }: { isOwner: boolean }) {
  const conn = useLucaConnection();
  const { activate, disconnect, sync } = useLucaMutations();
  const { updateFlags } = useTenantMutations();
  const toast = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const c = conn.data;

  if (!c) return <p style={{ color: 'var(--text-secondary)' }}>{t('common.loading')}</p>;

  const connected = c.hasCredentials && c.status === 'ACTIVE';
  return (
    <section
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
      data-testid="luca-connection"
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 15 }}>{t('luca.connection.title')}</strong>
        <span data-testid="luca-connected">
          {connected ? t('luca.connection.connected') : t('luca.connection.notConnected')}
        </span>
        <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          {t(`luca.connection.status.${c.status}`)}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 13 }}>
          {c.enabled ? t('luca.enabled') : t('luca.disabledFlag')}
        </span>
      </div>

      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
        {t('luca.connection.lastSuccess')}:{' '}
        {c.lastSuccessAt ? formatDateTime(c.lastSuccessAt) : t('luca.connection.never')}
      </div>
      {c.lastError ? (
        <p
          style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}
          data-testid="luca-last-error"
        >
          {lastErrorText(c.lastError)}
        </p>
      ) : null}
      {c.hasCredentials && !c.canActivate && c.status === 'PENDING' ? (
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
          {t('luca.connection.coldTest')}
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          className="btn"
          disabled={sync.isPending || !c.enabled || c.status !== 'ACTIVE'}
          onClick={() =>
            sync.mutate(undefined, {
              onSuccess: (r) => {
                if (r.status === 'ok') {
                  toast(t('luca.action.synced', { n: r.itemCount }));
                }
              },
            })
          }
        >
          {t('luca.action.sync')}
        </button>
        {isOwner ? (
          <>
            <button
              className="btn"
              disabled={updateFlags.isPending}
              onClick={() => updateFlags.mutate({ lucaEnabled: !c.enabled })}
            >
              {c.enabled ? t('luca.action.disable') : t('luca.action.enable')}
            </button>
            <button className="btn" onClick={() => setFormOpen((v) => !v)}>
              {t('luca.form.save')}
            </button>
            <button
              className="btn btn-primary"
              disabled={
                activate.isPending || !c.hasCredentials || !c.canActivate || c.status === 'ACTIVE'
              }
              onClick={() => activate.mutate()}
            >
              {t('luca.action.activate')}
            </button>
            <button
              className="btn"
              disabled={!c.hasCredentials}
              onClick={() => setConfirmDisconnect(true)}
            >
              {t('luca.action.disconnect')}
            </button>
          </>
        ) : null}
      </div>

      {sync.isError ? (
        <p style={{ color: 'var(--danger)', fontSize: 13 }}>{errorText(sync.error)}</p>
      ) : null}
      {activate.isError ? (
        <p style={{ color: 'var(--danger)', fontSize: 13 }}>{errorText(activate.error)}</p>
      ) : null}
      {sync.data?.status === 'ok' && sync.data.truncated ? (
        <p style={{ fontSize: 13, color: 'var(--warning)' }}>{t('luca.truncated')}</p>
      ) : null}
      {sync.data?.status === 'failed' ? (
        <p style={{ fontSize: 13, color: 'var(--danger)' }}>
          {lastErrorText(sync.data.code, sync.data.detail)}
        </p>
      ) : null}

      {isOwner && formOpen ? (
        <CredentialsForm connection={c} onDone={() => setFormOpen(false)} />
      ) : null}

      {confirmDisconnect ? (
        <ConfirmDialog
          title={t('luca.action.disconnect')}
          body={t('luca.action.disconnectConfirm')}
          confirmLabel={t('luca.action.disconnect')}
          tone="danger"
          pending={disconnect.isPending}
          error={disconnect.isError ? errorText(disconnect.error) : null}
          onConfirm={() =>
            disconnect.mutate(undefined, { onSuccess: () => setConfirmDisconnect(false) })
          }
          onCancel={() => setConfirmDisconnect(false)}
        />
      ) : null}
    </section>
  );
}

function CredentialsForm({
  connection,
  onDone,
}: {
  connection: LucaConnection;
  onDone: () => void;
}) {
  const [identification, setIdentification] = useState('');
  const [password, setPassword] = useState('');
  const [companyId, setCompanyId] = useState(
    connection.companyId ? String(connection.companyId) : '',
  );
  const [consent, setConsent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const submit = async () => {
    setPending(true);
    setError(null);
    try {
      const company = Number(companyId);
      await saveLucaConnection({
        identificationNumber: identification.trim(),
        password,
        consent: true,
        ...(companyId.trim() !== '' && Number.isInteger(company) && company > 0
          ? { companyId: company }
          : {}),
      });
      // Durum kartı yeni (PENDING, kimlik yazılı) hâli göstersin.
      await qc.invalidateQueries({ queryKey: ['luca'] });
      // Kimliği bellekte tutma: alanlar kayıttan hemen sonra temizlenir.
      setIdentification('');
      setPassword('');
      setConsent(false);
      toast(t('luca.form.saved'));
      onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setPending(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="luca-form">
      <span className="section-label">{t('luca.form.identification')}</span>
      <input
        value={identification}
        onChange={(e) => setIdentification(e.target.value)}
        inputMode="numeric"
        maxLength={11}
        autoComplete="off"
        style={{ height: 44 }}
      />
      <span className="section-label">{t('luca.form.password')}</span>
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="new-password"
        style={{ height: 44 }}
      />
      <span className="section-label">{t('luca.form.companyId')}</span>
      <input
        value={companyId}
        onChange={(e) => setCompanyId(e.target.value)}
        inputMode="numeric"
        autoComplete="off"
        style={{ height: 44 }}
      />
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 }}>
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
        <span>{t('luca.form.consent')}</span>
      </label>
      <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
        {t('luca.form.hint')}
      </p>
      {error ? <p style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</p> : null}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn" onClick={onDone}>
          {t('common.cancel')}
        </button>
        <button
          className="btn btn-primary"
          disabled={pending || !consent || identification.trim() === '' || password === ''}
          onClick={() => void submit()}
        >
          {t('luca.form.save')}
        </button>
      </div>
    </div>
  );
}

function useRange() {
  const today = istanbulDayString(new Date());
  const [from, setFrom] = useState(shiftIsoDay(today, -30));
  const [to, setTo] = useState(today);
  const [applied, setApplied] = useState({ from: shiftIsoDay(today, -30), to: today });
  const controls = (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      <label className="section-label" style={{ marginBottom: 0 }}>
        {t('luca.range.from')}{' '}
        <input
          type="date"
          value={from}
          max={to}
          onChange={(e) => setFrom(e.target.value)}
          style={{ height: 40, padding: '0 10px' }}
        />
      </label>
      <label className="section-label" style={{ marginBottom: 0 }}>
        {t('luca.range.to')}{' '}
        <input
          type="date"
          value={to}
          min={from}
          max={today}
          onChange={(e) => setTo(e.target.value)}
          style={{ height: 40, padding: '0 10px' }}
        />
      </label>
      <button
        className="btn"
        disabled={!from || !to || from > to}
        onClick={() => setApplied({ from, to })}
      >
        {t('luca.range.apply')}
      </button>
    </div>
  );
  return { applied, controls };
}

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 } as const;
const cell = {
  padding: '6px 8px',
  textAlign: 'left',
  borderBottom: '1px solid var(--border)',
} as const;

function DocumentsPanel() {
  const { applied, controls } = useRange();
  const [kind, setKind] = useState<LucaDocumentKind | ''>('');
  const docs = useLucaDocuments(kind || null, applied.from, applied.to);
  const items = docs.data?.items ?? [];
  return (
    <section
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      {controls}
      <select
        value={kind}
        onChange={(e) => setKind(e.target.value as LucaDocumentKind | '')}
        style={{ height: 40, padding: '0 10px', maxWidth: 240 }}
      >
        <option value="">{t('luca.kind.all')}</option>
        <option value="DESPATCH_IN">{t('luca.kind.DESPATCH_IN')}</option>
        <option value="EARCHIVE_OUT">{t('luca.kind.EARCHIVE_OUT')}</option>
      </select>
      {docs.data?.hasMore ? (
        <p style={{ fontSize: 13, color: 'var(--warning)' }}>
          {t('luca.docs.hasMore', { n: docs.data.limit })}
        </p>
      ) : null}
      {items.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)' }}>{t('luca.docs.empty')}</p>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={cell}>{t('luca.docs.col.kind')}</th>
              <th style={cell}>{t('luca.docs.col.number')}</th>
              <th style={cell}>{t('luca.docs.col.day')}</th>
              <th style={{ ...cell, textAlign: 'right' }}>{t('luca.docs.col.amount')}</th>
              <th style={cell}>{t('luca.docs.col.status')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id}>
                <td style={cell}>{t(`luca.kind.${d.kind}`)}</td>
                <td style={cell}>{d.documentNo}</td>
                <td style={cell}>{formatDate(`${d.issueDay}T12:00:00Z`)}</td>
                <td style={{ ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {d.amount !== null ? formatMoney(d.amount) : ''}
                </td>
                <td style={cell}>{d.status ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function shipmentLabel(s: LucaShipmentRef): string {
  const no = s.waybillNo ?? t('luca.recon.noWaybill');
  const day = s.day ? formatDate(`${s.day}T12:00:00Z`) : '';
  return `${no} · ${day} · ${s.qtyFull ?? ''} ${t('luca.recon.qty')}`;
}

function ReconciliationPanel() {
  const { applied, controls } = useRange();
  const rec = useLucaReconciliation(applied.from, applied.to);
  const { createLink, revokeLink } = useLucaMutations();
  const toast = useToast();
  const d = rec.data?.despatch;

  return (
    <section
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      {controls}
      {rec.isError ? <p style={{ color: 'var(--danger)' }}>{errorText(rec.error)}</p> : null}
      {createLink.isError ? (
        <p style={{ color: 'var(--danger)', fontSize: 13 }}>{errorText(createLink.error)}</p>
      ) : null}

      {d ? (
        <>
          <strong style={{ fontSize: 15 }}>{t('luca.recon.despatch')}</strong>
          <div
            style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}
            data-testid="luca-counts"
          >
            <span>
              {t('luca.recon.matched')}: {d.counts.matched}
            </span>
            <span>
              {t('luca.recon.lucaOnly')}: {d.counts.lucaOnly}
            </span>
            <span>
              {t('luca.recon.oursOnly')}: {d.counts.oursOnly}
            </span>
            <span>
              {t('luca.recon.conflicts')}: {d.counts.conflicts}
            </span>
          </div>

          {d.conflicts.map((c, i) => (
            <div key={`c${i}`} style={{ fontSize: 13, color: 'var(--danger)' }}>
              {t(`luca.recon.conflict.${c.reason}`)}:{' '}
              {c.documents.map((x) => x.documentNo ?? '').join(', ')}
            </div>
          ))}

          {d.lucaOnly.length > 0 ? (
            <div>
              <strong style={{ fontSize: 13 }}>{t('luca.recon.lucaOnly')}</strong>
              <table style={tableStyle}>
                <tbody>
                  {d.lucaOnly.map((row) => (
                    <tr key={row.document.id}>
                      <td style={cell}>{row.document.documentNo}</td>
                      <td style={cell}>
                        {row.candidates.length === 0 ? (
                          <span style={{ color: 'var(--text-secondary)' }}>
                            {t('luca.recon.noCandidates')}
                          </span>
                        ) : (
                          row.candidates.map((s) => (
                            <div
                              key={s.id}
                              style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                            >
                              <span>{shipmentLabel(s)}</span>
                              <button
                                className="btn"
                                disabled={createLink.isPending}
                                onClick={() =>
                                  createLink.mutate(
                                    { lucaDocumentId: row.document.id, shipmentId: s.id },
                                    { onSuccess: () => toast(t('luca.recon.linked')) },
                                  )
                                }
                              >
                                {t('luca.recon.link')}
                              </button>
                            </div>
                          ))
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {d.oursOnly.length > 0 ? (
            <div>
              <strong style={{ fontSize: 13 }}>{t('luca.recon.oursOnly')}</strong>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {d.oursOnly.map((s) => (
                  <li key={s.id}>{shipmentLabel(s)}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {d.matched.length > 0 ? (
            <div>
              <strong style={{ fontSize: 13 }}>{t('luca.recon.matched')}</strong>
              <table style={tableStyle}>
                <tbody>
                  {d.matched.map((m) => (
                    <tr key={m.document.id}>
                      <td style={cell}>{m.document.documentNo}</td>
                      <td style={cell}>{t(`luca.recon.via.${m.via}`)}</td>
                      <td style={cell}>
                        {m.shipments.map((s) => (
                          <div key={s.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <span>{shipmentLabel(s)}</span>
                            {s.linkId ? (
                              <button
                                className="btn"
                                disabled={revokeLink.isPending}
                                onClick={() => revokeLink.mutate(s.linkId!)}
                              >
                                {t('luca.recon.unlink')}
                              </button>
                            ) : null}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <strong style={{ fontSize: 15 }}>{t('luca.recon.archive')}</strong>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}>
            {t('luca.recon.archiveNote')}
          </p>
          {rec.data && rec.data.archive.length === 0 ? (
            <p style={{ color: 'var(--text-secondary)' }}>{t('luca.recon.empty')}</p>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={cell}>{t('luca.recon.col.day')}</th>
                  <th style={cell}>{t('luca.recon.col.lucaCount')}</th>
                  <th style={cell}>{t('luca.recon.col.lucaCancelled')}</th>
                  <th style={{ ...cell, textAlign: 'right' }}>{t('luca.recon.col.lucaTotal')}</th>
                  <th style={cell}>{t('luca.recon.col.ourCount')}</th>
                  <th style={{ ...cell, textAlign: 'right' }}>{t('luca.recon.col.ourTotal')}</th>
                  <th style={{ ...cell, textAlign: 'right' }}>{t('luca.recon.col.ourDeposit')}</th>
                  <th style={cell}>{t('luca.recon.col.ourJugs')}</th>
                </tr>
              </thead>
              <tbody>
                {rec.data?.archive.map((r) => (
                  <tr key={r.day}>
                    <td style={cell}>{formatDate(`${r.day}T12:00:00Z`)}</td>
                    <td style={cell}>{r.luca.count}</td>
                    <td style={cell}>{r.luca.cancelledCount}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{formatMoney(r.luca.total)}</td>
                    <td style={cell}>{r.ours.count}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{formatMoney(r.ours.total)}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>{formatMoney(r.ours.deposit)}</td>
                    <td style={cell}>{r.ours.soldJugs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      ) : null}
    </section>
  );
}
